// components/XRechnungViewer.jsx

import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner, Alert } from "react-bootstrap";
import { FaFileInvoice, FaDownload, FaPrint, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";

export default function XRechnungViewer({ show, onHide, xmlUrl, title }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);

  useEffect(() => {
    if (show && xmlUrl) {
      fetchAndParseXML();
    }
  }, [show, xmlUrl]);

  const fetchAndParseXML = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(xmlUrl);
      if (!response.ok) throw new Error("Failed to load XML");

      const xmlText = await response.text();
      const parsed = parseXRechnung(xmlText);
      setInvoiceData(parsed);
    } catch (err) {
      console.error("Error loading XML:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ دالة تحليل XML
  const parseXRechnung = (xmlText) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "text/xml");

    const getValue = (tagName) => {
      const element = xmlDoc.getElementsByTagName(tagName)[0];
      return element ? element.textContent : "";
    };

    const getValueNS = (ns, tagName) => {
      const elements = xmlDoc.getElementsByTagNameNS(ns, tagName);
      return elements.length > 0 ? elements[0].textContent : "";
    };

    // ✅ استخراج البيانات من XML
    return {
      invoiceNumber: getValueNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "ID"),
      issueDate: getValueNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "IssueDate"),
      dueDate: getValueNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "DueDate"),
      currency: getValueNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "DocumentCurrencyCode"),
      
      // ✅ المورد (Supplier)
      supplier: {
        name: getValueNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "RegistrationName"),
        street: getValueNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "StreetName"),
        city: getValueNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "CityName"),
        postalCode: getValueNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "PostalZone"),
        country: getValueNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "IdentificationCode"),
        taxId: getValueNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "CompanyID"),
        email: getValueNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "ElectronicMail"),
        phone: getValueNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "Telephone"),
      },
      
      // ✅ العميل (Customer)
      customer: {
        name: getValue("Name"),
        street: getValue("StreetName"),
        postalCode: getValue("PostalZone"),
        email: getValue("EndpointID"),
      },
      
      // ✅ المبالغ
      amounts: {
        lineExtension: getValue("LineExtensionAmount"),
        taxExclusive: getValue("TaxExclusiveAmount"),
        taxInclusive: getValue("TaxInclusiveAmount"),
        payable: getValue("PayableAmount"),
        taxAmount: getValue("TaxAmount"),
        taxPercent: getValue("Percent"),
      },
      
      // ✅ بيانات البنك
      bank: {
        iban: getValue("ID"),
        bankName: getValue("Name"),
        bic: getValue("ID"),
      },
      
      // ✅ بنود الفاتورة
      lines: Array.from(xmlDoc.getElementsByTagNameNS(
        "urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2",
        "InvoiceLine"
      )).map((line) => ({
        id: line.getElementsByTagNameNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "ID")[0]?.textContent || "",
        quantity: line.getElementsByTagNameNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "InvoicedQuantity")[0]?.textContent || "",
        description: line.getElementsByTagNameNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "Description")[0]?.textContent || "",
        name: line.getElementsByTagNameNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "Name")[0]?.textContent || "",
        lineTotal: line.getElementsByTagNameNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "LineExtensionAmount")[0]?.textContent || "",
        unitPrice: line.getElementsByTagNameNS("urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2", "PriceAmount")[0]?.textContent || "",
      })),
    };
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fw-bold">
          <FaFileInvoice className="me-2 text-warning" />
          {title || t("invoice.title", "XRechnung / E-Rechnung")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4" style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: "#ff7b00" }} />
            <p className="mt-2">{t("common.loading", "Laden...")}</p>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {invoiceData && !loading && (
          <div className="xrechnung-viewer" id="xrechnung-print">
            {/* Header */}
            <div
              className="d-flex justify-content-between align-items-start mb-4 pb-3"
              style={{ borderBottom: "2px solid #ff7b00" }}
            >
              <div>
                <h4 className="fw-bold mb-1" style={{ color: "#ff7b00" }}>
                  {t("invoice.title", "RECHNUNG")}
                </h4>
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                  {t("invoice.number", "Rechnungsnummer")}:{" "}
                  <strong>{invoiceData.invoiceNumber}</strong>
                </p>
              </div>
              <div className="text-end" style={{ fontSize: 13 }}>
                <p className="mb-1">
                  <strong>{t("invoice.issueDate", "Rechnungsdatum")}:</strong>{" "}
                  {invoiceData.issueDate}
                </p>
                <p className="mb-0">
                  <strong>{t("invoice.dueDate", "Fälligkeitsdatum")}:</strong>{" "}
                  {invoiceData.dueDate}
                </p>
              </div>
            </div>

            {/* Supplier & Customer */}
            <div className="row g-4 mb-4">
              <div className="col-md-6">
                <div
                  className="p-3 rounded h-100"
                  style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}
                >
                  <h6 className="fw-bold text-muted mb-2" style={{ fontSize: 12 }}>
                    {t("invoice.supplier", "RECHNUNGSSTELLER")}
                  </h6>
                  <p className="fw-bold mb-1">{invoiceData.supplier.name}</p>
                  <p className="mb-0" style={{ fontSize: 13 }}>
                    {invoiceData.supplier.street}
                    <br />
                    {invoiceData.supplier.postalCode} {invoiceData.supplier.city}
                    <br />
                    {invoiceData.supplier.country}
                  </p>
                  <p className="mb-0 mt-2" style={{ fontSize: 12 }}>
                    <strong>USt-IdNr:</strong> {invoiceData.supplier.taxId}
                  </p>
                </div>
              </div>

              <div className="col-md-6">
                <div
                  className="p-3 rounded h-100"
                  style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}
                >
                  <h6 className="fw-bold text-muted mb-2" style={{ fontSize: 12 }}>
                    {t("invoice.customer", "RECHNUNGSEMPFÄNGER")}
                  </h6>
                  <p className="fw-bold mb-1">{invoiceData.customer.name}</p>
                  <p className="mb-0" style={{ fontSize: 13 }}>
                    {invoiceData.customer.street}
                    <br />
                    {invoiceData.customer.postalCode}
                  </p>
                  {invoiceData.customer.email && (
                    <p className="mb-0 mt-2" style={{ fontSize: 12 }}>
                      {invoiceData.customer.email}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Invoice Lines */}
            <div className="mb-4">
              <h6 className="fw-bold mb-3">{t("invoice.items", "Positionen")}</h6>
              <div className="table-responsive">
                <table className="table table-bordered mb-0" style={{ fontSize: 13 }}>
                  <thead style={{ background: "#ff7b00", color: "#fff" }}>
                    <tr>
                      <th>#</th>
                      <th>{t("invoice.description", "Beschreibung")}</th>
                      <th className="text-center">{t("invoice.quantity", "Menge")}</th>
                      <th className="text-end">{t("invoice.unitPrice", "Einzelpreis")}</th>
                      <th className="text-end">{t("invoice.total", "Gesamt")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.lines.map((line, idx) => (
                      <tr key={idx}>
                        <td>{line.id}</td>
                        <td>
                          <strong>{line.name}</strong>
                          <br />
                          <small className="text-muted">{line.description}</small>
                        </td>
                        <td className="text-center">{line.quantity}</td>
                        <td className="text-end">
                          {parseFloat(line.unitPrice).toFixed(2)} €
                        </td>
                        <td className="text-end fw-bold">
                          {parseFloat(line.lineTotal).toFixed(2)} €
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="row justify-content-end mb-4">
              <div className="col-md-6">
                <div className="p-3 rounded" style={{ background: "#f8f9fa" }}>
                  <div className="d-flex justify-content-between mb-2">
                    <span>{t("invoice.subtotal", "Zwischensumme (netto)")}:</span>
                    <strong>{parseFloat(invoiceData.amounts.lineExtension).toFixed(2)} €</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>
                      {t("invoice.vat", "MwSt")} ({invoiceData.amounts.taxPercent}%):
                    </span>
                    <strong>{parseFloat(invoiceData.amounts.taxAmount).toFixed(2)} €</strong>
                  </div>
                  <hr className="my-2" />
                  <div
                    className="d-flex justify-content-between"
                    style={{ fontSize: 16, color: "#ff7b00" }}
                  >
                    <strong>{t("invoice.total", "Gesamtbetrag")}:</strong>
                    <strong>
                      {parseFloat(invoiceData.amounts.payable).toFixed(2)} €
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Bank Info */}
            <div
              className="p-3 rounded mb-3"
              style={{ background: "#fff8f0", border: "1px solid #ffd4a3" }}
            >
              <h6 className="fw-bold mb-2" style={{ color: "#ff7b00" }}>
                {t("invoice.bankInfo", "Bankverbindung")}
              </h6>
              <p className="mb-1" style={{ fontSize: 13 }}>
                <strong>IBAN:</strong> {invoiceData.bank.iban}
              </p>
              <p className="mb-0" style={{ fontSize: 13 }}>
                <strong>{t("invoice.bankName", "Bank")}:</strong>{" "}
                {invoiceData.bank.bankName}
              </p>
            </div>

            {/* Footer */}
            <div
              className="text-center text-muted pt-3"
              style={{ fontSize: 11, borderTop: "1px solid #e9ecef" }}
            >
              {t(
                "invoice.footer",
                "Vielen Dank für Ihr Vertrauen! Diese Rechnung wurde elektronisch erstellt und ist ohne Unterschrift gültig."
              )}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="secondary" onClick={onHide}>
          <FaTimes className="me-2" />
          {t("common.close", "Schließen")}
        </Button>

        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            onClick={() => window.open(xmlUrl, "_blank")}
          >
            <FaDownload className="me-2" />
            {t("invoice.downloadXml", "XML herunterladen")}
          </Button>
          <Button
            variant="warning"
            onClick={handlePrint}
            style={{ color: "#fff" }}
          >
            <FaPrint className="me-2" />
            {t("invoice.print", "Drucken")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}